// src/pages/AcceptInvite.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, AlertCircle, Eye, EyeOff, Check } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Input, Label } from '@/components/ui/input.jsx';
import { apiCall } from '../utils/api';
import PasswordRequirements, { passwordMeetsRules } from '@/components/ui/PasswordRequirements.jsx';


export default function AcceptInvitation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Invitation metadata (loaded from GET /invitations/accept)
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState('');

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);

  // Load invitation on mount
  useEffect(() => {
    if (!token) {
      setLoadError('Missing invitation token. This link may be malformed.');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const res = await apiCall(
          `/invitations/accept?token=${encodeURIComponent(token)}`,
          { method: 'GET' }
        );
        if (res.ok) {
          const data = await res.json();
          setInvite(data);
        } else {
          const errData = await res.json().catch(() => ({}));
          setLoadError(errData.detail || 'This invitation is no longer valid.');
        }
      } catch (err) {
        console.error('Failed to load invitation:', err);
        setLoadError('Could not reach the server. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    // Client-side validation (server re-validates)
    if (!passwordMeetsRules(password)) {
      setSubmitError('Password does not meet the requirements below.');
      return;
    }

    if (password !== confirmPassword) {

      setSubmitError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiCall('/invitations/accept', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      if (res.ok) {
        setSuccess(true);
        // Give user a moment to see the confirmation, then redirect to login
        setTimeout(() => {
          navigate('/login', {
            state: { message: 'Account created. Please log in.' },
          });
        }, 1500);
      } else {
        const errData = await res.json().catch(() => ({}));
        const detail = errData.detail;
        setSubmitError(
          Array.isArray(detail) ? detail.join(' ')
          : (detail || 'Could not accept invitation.')
        );

      }
    } catch (err) {
      console.error('Accept error:', err);
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────
  // Loading state
  // ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <AuthShell>
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          <p className="mt-4 text-sm text-gray-400">Loading invitation...</p>
        </div>
      </AuthShell>
    );
  }

  // ─────────────────────────────────────────────────────
  // Invalid / expired / revoked invitation
  // ─────────────────────────────────────────────────────
  if (loadError) {
    return (
      <AuthShell>
        <div className="text-center py-8">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <h2 className="mt-4 text-lg font-semibold text-white">
            Invitation unavailable
          </h2>
          <p className="mt-2 text-sm text-gray-400">{loadError}</p>
          <Button
            className="mt-6"
            onClick={() => navigate('/login')}
          >
            Go to sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  // ─────────────────────────────────────────────────────
  // Success state (brief, before redirect)
  // ─────────────────────────────────────────────────────
  if (success) {
    return (
      <AuthShell>
        <div className="text-center py-8">
          <div className="mx-auto h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center">
            <Check className="h-6 w-6 text-green-400" />
          </div>
          <h2 className="mt-4 text-lg font-semibold text-white">
            Account created!
          </h2>
          <p className="mt-2 text-sm text-gray-400">
            Redirecting to sign in...
          </p>
        </div>
      </AuthShell>
    );
  }

  // ─────────────────────────────────────────────────────
  // Main form
  // ─────────────────────────────────────────────────────
  return (
    <AuthShell>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-white">
          Join {invite.organization_name}
        </h2>
        <p className="mt-2 text-sm text-gray-400">
          You've been invited to join as a <strong>{invite.role}</strong>.
          Set a password below to create your account.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email (read-only) */}
        <div>
          <Label className="text-gray-300">Email</Label>
          <div className="mt-1 relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              value={invite.email}
              disabled
              className="pl-10 bg-[#2a2a2a] text-gray-400 cursor-not-allowed"
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <Label className="text-gray-300">Password</Label>
          <div className="mt-1 relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 12 characters"
              required
              className="pl-10 pr-10"
            />     
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <PasswordRequirements password={password} />          
        </div>

        {/* Confirm password */}
        <div>
          <Label className="text-gray-300">Confirm password</Label>
          <div className="mt-1 relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="pl-10"
            />
          </div>
        </div>

        {submitError && (
          <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-400">{submitError}</p>
          </div>
        )}

        <Button type="submit" disabled={submitting || !passwordMeetsRules(password) || password !== confirmPassword} className="w-full">

          {submitting ? 'Creating account...' : 'Create account'}
        </Button>
      </form>
    </AuthShell>
  );
}

// Shared dark-themed auth shell so the page matches Login.jsx aesthetic.
function AuthShell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img
            src="/connexlogo2-transparent.png"
            alt="Connex"
            className="h-16 w-auto"
          />
        </div>
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-8">
          {children}
        </div>
      </div>
    </div>
  );
}